import type { FC } from "react"
import { memo, useCallback } from "react"
import { BiChevronDown } from "react-icons/bi"

import {
  Box,
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Stack,
  Text,
} from "@chakra-ui/react"

import { ArrowRightIcon } from "../../elements/icons/ArrowRightIcon"

import { PagingLegend } from "./PagingLegend"
import { PagingNumbers } from "./PagingNumbers"

export type PagingProps = {
  itemsPerPageOptions: number[]
  itemsTotal: number
  itemsPerPage: number
  selectedPage: number
  setSelectedPage: (page: number) => void
  setItemsPerPage: (value: number) => void
}

export const Paging = memo(
  ({
    itemsTotal,
    itemsPerPageOptions,
    itemsPerPage,
    setItemsPerPage,
    selectedPage,
    setSelectedPage,
  }: PagingProps) => {
    const pageCount = Math.ceil(itemsTotal / itemsPerPage)

    const handleScrollTop = useCallback(() => {
      window.scrollTo({
        top: 0,
      })
    }, [])

    return (
      <Stack
        direction={{ base: "column", md: "row" }}
        spacing={{ base: 3, md: 6 }}
        align="center"
        alignContent="flex-end"
        justifyContent="space-between"
      >
        <ItemsPerPageMenu
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          itemsPerPageOptions={itemsPerPageOptions}
        />

        <Stack
          direction={{ base: "column", md: "row" }}
          spacing={{ base: 3, md: 0 }}
          align="center"
        >
          <PagingLegend
            display={{ base: "none", md: "block" }}
            itemsTotal={itemsTotal}
            itemsPerPage={itemsPerPage}
            selectedPage={selectedPage}
            mr={[0, 0, 0, 5]}
          />

          <Flex alignItems="center">
            <PagingNumbers
              pageCount={pageCount}
              selectedPage={selectedPage}
              onPageClick={(page) => {
                setSelectedPage(page)
                handleScrollTop()
              }}
            />

            {selectedPage < pageCount && (
              <Box
                mb="2px"
                ml="3px"
                cursor="pointer"
                onClick={() => {
                  setSelectedPage(selectedPage + 1)
                  handleScrollTop()
                }}
              >
                <ArrowRightIcon />
              </Box>
            )}
          </Flex>
        </Stack>
      </Stack>
    )
  },
)

const ItemsPerPageMenu: FC<
  Pick<PagingProps, "itemsPerPage" | "setItemsPerPage" | "itemsPerPageOptions">
> = memo(({ itemsPerPage, setItemsPerPage, itemsPerPageOptions }) => {
  const onSetItemsPerPage = useCallback(
    (value: string) => {
      const numberValue = Number(value)
      if (!isNaN(numberValue) && itemsPerPageOptions.includes(numberValue)) {
        setItemsPerPage(numberValue)
        handleScrollTop()
      }
    },
    [setItemsPerPage, itemsPerPageOptions],
  )

  const handleScrollTop = useCallback(() => {
    window.scrollTo({
      top: 0,
    })
  }, [])

  return (
    <Stack w={{ base: "full", md: "auto" }} direction="row" align="center">
      <Text
        fontSize={{ base: 12, md: 14 }}
        color="neutrals.900"
        userSelect="none"
        display={{ base: "none", lg: "block" }}
      >
        Items per page:
      </Text>

      <Menu>
        <MenuButton
          as={Button}
          w={{ base: "full", md: "auto" }}
          variant="dropdown"
          colorScheme="tertiary"
          rightIcon={<Icon as={BiChevronDown} fontSize={25} />}
        >
          <Stack align="center" justify="space-between" direction="row">
            <Box
              as="span"
              fontSize={12}
              display={{ base: "inline-block", lg: "none" }}
            >
              Items per page
            </Box>

            <Box as="span" data-testid="pagingItemsPerPage">
              {itemsPerPage}
            </Box>
          </Stack>
        </MenuButton>

        <MenuList>
          <MenuOptionGroup
            value={itemsPerPage.toString()}
            type="radio"
            onChange={onSetItemsPerPage}
          >
            {itemsPerPageOptions.map((count) => (
              <MenuItemOption value={count.toString()} key={count}>
                {count}
              </MenuItemOption>
            ))}
          </MenuOptionGroup>
        </MenuList>
      </Menu>
    </Stack>
  )
})
